import * as styles from "./NumberField.css";

import { clsx } from "clsx";
import type { JSX } from "react";

import { Field as BaseField } from "@base-ui/react/field";
import { NumberField as BaseNumberField } from "@base-ui/react/number-field";
import { Icon } from "@frontend/components/atoms/Icon";
import { Switch } from "@frontend/components/atoms/Switch";
import { useControlContext } from "@frontend/contexts";

import type { NumberFieldProps } from "./NumberField.types";

export function NumberField({
  size,
  variant,
  layout = "stacked",
  align,
  invalid,
  success,
  leftSection,
  rightSection,
  toggle,
  className,
  ...props
}: NumberFieldProps): JSX.Element {
  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;

  const toggleSection = toggle ? (
    <BaseField.Root
      data-slot="number-field-toggle"
      className={styles.toggleSection}
    >
      <Switch size={resolvedSize} {...toggle} />
    </BaseField.Root>
  ) : null;

  const input = (
    <>
      {leftSection ? (
        <span
          data-slot="number-field-left-section"
          className={styles.leftSection}
        >
          {leftSection}
        </span>
      ) : null}

      <BaseNumberField.Input
        data-slot="number-field-input"
        className={styles.input({ align })}
      />

      {rightSection ? (
        <span
          data-slot="number-field-right-section"
          className={styles.rightSection}
        >
          {rightSection}
        </span>
      ) : null}
    </>
  );

  if (layout === "text") {
    return (
      <BaseNumberField.Root
        data-slot="number-field"
        className={styles.textRoot}
        {...props}
      >
        <BaseNumberField.Group
          data-slot="number-field-group"
          className={clsx(styles.textGroup({ invalid, success }), className)}
        >
          <BaseNumberField.Decrement
            data-slot="number-field-decrement"
            className={styles.textDecrement}
          >
            <Icon name="ChevronLeft" size="sm" />
          </BaseNumberField.Decrement>
          <BaseNumberField.Input
            data-slot="number-field-input"
            className={styles.textInput({ align })}
            style={({ inputValue }) => ({
              width: `${Math.max(inputValue.length, 1)}ch`,
            })}
          />
          <BaseNumberField.Increment
            data-slot="number-field-increment"
            className={styles.textIncrement}
          >
            <Icon name="ChevronRight" size="sm" />
          </BaseNumberField.Increment>
        </BaseNumberField.Group>
      </BaseNumberField.Root>
    );
  }

  if (layout === "inline") {
    return (
      <BaseNumberField.Root data-slot="number-field" {...props}>
        <BaseNumberField.Group
          data-slot="number-field-group"
          className={clsx(
            styles.group({ size: resolvedSize, variant, invalid, success }),
            className,
          )}
        >
          {toggleSection}
          <BaseNumberField.Decrement
            data-slot="number-field-decrement"
            className={styles.inlineDecrement}
          >
            <Icon name="Minus" size="sm" />
          </BaseNumberField.Decrement>
          {input}
          <BaseNumberField.Increment
            data-slot="number-field-increment"
            className={styles.inlineIncrement}
          >
            <Icon name="Plus" size="sm" />
          </BaseNumberField.Increment>
        </BaseNumberField.Group>
      </BaseNumberField.Root>
    );
  }

  return (
    <BaseNumberField.Root data-slot="number-field" {...props}>
      <BaseNumberField.Group
        data-slot="number-field-group"
        className={clsx(
          styles.group({ size: resolvedSize, variant, invalid, success }),
          className,
        )}
      >
        {toggleSection}
        {input}
        <div className={styles.stackedSteppers}>
          <BaseNumberField.Increment
            data-slot="number-field-increment"
            className={styles.stackedStepper}
          >
            <Icon name="ChevronUp" size="sm" />
          </BaseNumberField.Increment>
          <BaseNumberField.Decrement
            data-slot="number-field-decrement"
            className={styles.stackedStepper}
          >
            <Icon name="ChevronDown" size="sm" />
          </BaseNumberField.Decrement>
        </div>
      </BaseNumberField.Group>
    </BaseNumberField.Root>
  );
}
